import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { SearchIcon, ShoppingCartIcon, UserIcon, HeartIcon, GlobeIcon, MessageCircleIcon, PhoneIcon, XIcon, TrendingUpIcon, PlusIcon, PackageIcon, ClipboardListIcon, MapPinIcon, UserPlusIcon, ChevronRightIcon, TrashIcon, ChevronDownIcon, ClockIcon, MenuIcon, ScanBarcodeIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { useStore } from '../../lib/store';
import { trackViewCart } from '../../lib/analytics/events';
import { haptics } from '../../lib/utils/haptics';
import { AnnouncementBar } from './AnnouncementBar';
import { AuthModal } from '../modals/AuthModal';
import { LocationModal } from '../modals/LocationModal';
import { OrderTrackingModal } from '../modals/OrderTrackingModal';
import { TRENDING_SEARCHES, TRENDING_PRODUCTS } from '../../data/searchData';
interface DesktopHeaderProps {
  locationModalOpen: boolean;
  setLocationModalOpen: (open: boolean) => void;
  selectedLocation: {
    name: string;
  } | null;
  setSelectedLocation: (location: {
    name: string;
  } | null) => void;
  disableShrink?: boolean;
  showAnnouncement?: boolean;
}
export function DesktopHeader({
  locationModalOpen,
  setLocationModalOpen,
  selectedLocation,
  setSelectedLocation,
  disableShrink = false,
  showAnnouncement = false
}: DesktopHeaderProps) {
  const navigate = useNavigate();
  const {
    cart: cartItems,
    wishlist: wishlistItems,
    language,
    setLanguage,
    cartCount,
    cartTotal,
    searchQuery,
    setSearchQuery,
    addToCart,
    recentSearches,
    addRecentSearch,
    clearRecentSearches
  } = useStore();
  const [scrolled, setScrolled] = useState(false);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [showAccountMenu, setShowAccountMenu] = useState(false);
  const [showWishlistMenu, setShowWishlistMenu] = useState(false);
  const [showCartMenu, setShowCartMenu] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [orderTrackingModalOpen, setOrderTrackingModalOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchDropdownRef = useRef<HTMLDivElement>(null);
  const wishlistCount = wishlistItems.length;
  const cartTotalValue = cartTotal();
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchDropdownRef.current && !searchDropdownRef.current.contains(event.target as Node) && searchInputRef.current && !searchInputRef.current.contains(event.target as Node)) {
        setShowSearchDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setShowSearchDropdown(true);
  };
  const handleMouseEnter = (menu: string) => {
    if (menu === 'account') setShowAccountMenu(true);
    if (menu === 'wishlist') setShowWishlistMenu(true);
    if (menu === 'cart') {
      setShowCartMenu(true);
      if (cartItems.length > 0) {
        trackViewCart(cartItems.map(item => ({
          item_id: item.id.toString(),
          item_name: item.name,
          price: item.price,
          quantity: item.quantity
        })));
      }
    }
  };
  const handleMouseLeave = (menu: string) => {
    if (menu === 'account') setShowAccountMenu(false);
    if (menu === 'wishlist') setShowWishlistMenu(false);
    if (menu === 'cart') setShowCartMenu(false);
  };
  const goals = [{
    name: 'Muscle Building',
    icon: '💪',
    slug: 'muscle-building'
  }, {
    name: 'Weight Loss',
    icon: '⚖️',
    slug: 'weight-loss'
  }, {
    name: 'Energy & Endurance',
    icon: '⚡',
    slug: 'energy-endurance'
  }, {
    name: 'Recovery',
    icon: '🔄',
    slug: 'recovery'
  }, {
    name: 'General Health',
    icon: '❤️',
    slug: 'general-health'
  }, {
    name: 'Mass Gain',
    icon: '📈',
    slug: 'mass-gain'
  }];
  const getSearchContent = () => {
    if (searchQuery.length === 0) return null;
    return <div className="w-full" data-id="element-2515" data-mp="18q2uqzmqo8m">
        <h3 className="text-xs font-bold text-[#62617A] uppercase tracking-wider mb-4" data-id="element-2516" data-mp="17ngmwd959z1">
          Matching Products
        </h3>
        <div className="space-y-4" data-id="element-2517" data-mp="22zthdrcn4myg">
          {TRENDING_PRODUCTS.slice(0, 4).map(product => <div key={product.id} className="flex items-center gap-3 p-2 hover:bg-[#F3F2FA] rounded-xl transition-colors group cursor-pointer" onClick={() => navigate(`/product/${product.id}`)} data-id="element-2518" data-mp="1yy7xhm1ov8er">
              <div className="w-16 h-16 rounded-lg overflow-hidden bg-white flex-shrink-0 border border-[#DEDCF0]" data-id="element-2519" data-mp="1sul7hrl7pg6h">
                <img src={product.image} alt={product.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" data-id="element-2520" data-mp="1f47ydlwl3vfk" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-2521" data-mp="1a551ioqrcvio">
                <h4 className="text-sm font-medium text-[#333273] line-clamp-2 leading-tight mb-1 group-hover:text-[#333273] transition-colors" data-id="element-2522" data-mp="1a6yc2f4hrzvy">
                  {product.name}
                </h4>
                <div className="flex items-center gap-2" data-id="element-2523" data-mp="1158jv72scwx4">
                  <span className="text-sm font-bold text-[#333273]" data-id="element-2524" data-mp="2qj3qoao5lvel">
                    {product.price.toFixed(2)} SAR
                  </span>
                  {product.discount > 0 && <span className="text-[10px] text-[#62617A] line-through" data-id="element-2525" data-mp="2t4ku45z6k7gg">
                      {product.originalPrice?.toFixed(2)}
                    </span>}
                </div>
                {product.discount > 0 && <div className="mt-1 inline-block bg-red-100 text-red-700 text-[9px] font-bold px-1.5 py-0.5 rounded-md uppercase tracking-tighter" data-id="element-2526" data-mp="2k3n10iqa5kcn">
                    {product.discount}% OFF
                  </div>}
              </div>
              <button onClick={e => {
            e.stopPropagation();
            haptics.success();
            addToCart({
              id: product.id,
              name: product.name,
              price: product.price,
              image: product.image
            });
            toast.success(`${product.name} added to cart!`);
          }} className="w-8 h-8 flex items-center justify-center bg-white border border-[#DEDCF0] text-[#333273] rounded-full hover:bg-[#333273] hover:text-white transition-all shadow-sm" data-id="element-2527" data-mp="37sfqpxgb6vyt">
                <PlusIcon className="w-4 h-4" strokeWidth={1.5} data-id="element-2528" data-mp="6x6vy01zvz9j" />
              </button>
            </div>)}
        </div>
      </div>;
  };
  return <div className="fixed left-0 right-0 top-0 z-[100] hidden transition-[box-shadow] duration-200 md:block" data-id="element-2529" data-mp="gvdz9g3d2it2">
      {showAnnouncement && <AnnouncementBar desktopOnly data-id="element-2530" data-mp="kvogxq0imxnr" />}
      {/* Main Header */}
      <div className="bg-white border-b border-[#E3E1EF]" data-id="element-2531" data-mp="pv11g43wzsn6">
        <div className="container mx-auto px-4" data-id="element-2532" data-mp="ptg7ogpfcdqd">
          <motion.div animate={{
          height: scrolled ? 60 : 72
        }} transition={{
          duration: 0.3
        }} className="flex items-center justify-between" data-id="element-2533" data-mp="ulzix70n2ws0">
            {/* Logo */}
            <button onClick={() => navigate('/')} className="flex-shrink-0" data-id="element-2534" data-mp="18m0lcqwjyc9l">
              <motion.img animate={{
              height: scrolled ? 32 : 44
            }} src="https://cdn.magicpatterns.com/uploads/enT9iVMZr5bUJi1w413sX2/Layer_1_(1).png" alt="Dawaak" className={scrolled ? 'h-8 w-auto' : 'h-11 w-auto'} data-id="element-2535" data-mp="18jxwia74yp6g" />
            </button>

            {/* Deliver To & Search */}
            <div className={`flex-1 ${scrolled ? 'max-w-6xl md:mx-4 lg:mx-6' : 'max-w-5xl md:mx-4 lg:mx-10'} flex items-center md:gap-3 lg:gap-6`} data-id="element-2536" data-mp="1ywv7sl7x0s4c">
              <button onClick={() => setLocationModalOpen(true)} className={`flex items-center gap-2 hover:opacity-80 transition-opacity whitespace-nowrap`} data-id="element-2537" data-mp="168pnfzkrx551">
                <MapPinIcon className={`${scrolled ? 'w-4 h-4' : 'w-5 h-5'} text-[#333273]`} strokeWidth={1.5} data-id="element-2538" data-mp="1g4spz6jjf5si" />
                <div className="text-left hidden lg:block" data-id="element-2539" data-mp="nheyvp2it4gb">
                  {!scrolled && <div className="text-[10px] text-[#62617A] leading-none mb-0.5" data-id="element-2540" data-mp="2z3qljoxh3zhb">
                      Deliver to
                    </div>}
                  <div className={`${scrolled ? 'text-xs' : 'text-sm'} font-normal text-[#333273]`} data-id="element-2541" data-mp="33x1wa15ep0ei">
                    {selectedLocation ? selectedLocation.name : 'Select Location'}
                  </div>
                </div>
                <ChevronDownIcon className="w-4 h-4 text-[#62617A] ml-1 hidden lg:block" strokeWidth={1.5} data-id="element-2542" data-mp="3hsx4nd26it3n" />
              </button>

              <div className="flex-1 relative" data-id="element-2543" data-mp="38uuzclbexeak">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" data-id="element-2544" data-mp="38w63im8rso27">
                  <SearchIcon className="w-4 h-4 text-[#62617A]" strokeWidth={1.5} data-id="element-2545" data-mp="3drbq7n9zy6rc" />
                </div>
                <input type="text" placeholder="Search for supplements, brands, goals..." className={`w-full ${scrolled ? 'h-10' : 'h-11'} bg-[#F3F2FA] border border-transparent rounded-lg pl-10 pr-12 focus:outline-none focus:bg-white focus:border-[#333273] focus:ring-1 focus:ring-[#333273] transition-all text-sm text-[#333273] placeholder-[#62617A] font-normal`} value={searchQuery} onChange={handleSearchInputChange} onFocus={() => setShowSearchDropdown(true)} ref={searchInputRef} data-id="element-2546" data-mp="1xgzmxkbw7jn1" />
                <button className="absolute right-4 top-1/2 -translate-y-1/2 text-[#333273] hover:text-blue-700 transition-colors" data-id="element-2547" data-mp="jzhqen4qfqhm">
                  <ScanBarcodeIcon className="w-5 h-5" strokeWidth={1.5} data-id="element-2548" data-mp="619w6mz2z3a5" />
                </button>
                <AnimatePresence data-id="element-2549" data-mp="azul3q8vu0ja">
                  {showSearchDropdown && <motion.div ref={searchDropdownRef} initial={{
                  opacity: 0,
                  y: 10
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: 10
                }} className="absolute top-full left-0 right-0 mt-2 w-full bg-white rounded-xl shadow-2xl border border-[#DEDCF0] z-50 max-h-[80vh] overflow-y-auto p-4" data-id="element-2550" data-mp="20dpyjcor9x9q">
                      {getSearchContent()}
                    </motion.div>}
                </AnimatePresence>
              </div>
            </div>

            {/* User Actions */}
            <div className="flex items-center md:space-x-4 lg:space-x-6" data-id="element-2551" data-mp="377e0bf6rnphz">
              {/* Language Selector */}
              <div className="relative group hidden lg:block" data-id="element-2552" data-mp="334hg5k1kvlbx">
                <button className="flex items-center gap-1 text-[#62617A] hover:text-[#333273] transition-colors" data-id="element-2553" data-mp="1cuxn1ufjzb3m">
                  <GlobeIcon className="w-5 h-5" strokeWidth={1.5} data-id="element-2554" data-mp="18udoqnj8u1fw" />
                  <span className="text-sm font-normal" data-id="element-2555" data-mp="37ydu2jktv50n">{language === 'ar' ? 'AR' : 'EN'}</span>
                  <ChevronDownIcon className="w-3 h-3" strokeWidth={1.5} data-id="element-2556" data-mp="3cvm4fxzqi4rc" />
                </button>
                <div className="absolute right-0 mt-2 w-32 bg-white border border-[#DEDCF0] rounded-lg shadow-lg z-50 hidden group-hover:block" data-id="element-2557" data-mp="3hv8nwp65oh8l">
                  <div className="py-1" data-id="element-2558" data-mp="3msidq5xgiuwy">
                    <button onClick={() => setLanguage('en')} className={`w-full text-left block px-4 py-2 text-sm ${language === 'en' ? 'bg-[#F3F2FA] text-[#333273] font-normal' : 'text-[#62617A] hover:bg-[#F3F2FA] hover:text-[#333273]'}`} data-id="element-2559" data-mp="3qwq0vyo9o2t2">
                      English
                    </button>
                    <button onClick={() => setLanguage('ar')} className={`w-full text-left block px-4 py-2 text-sm ${language === 'ar' ? 'bg-[#F3F2FA] text-[#333273] font-normal' : 'text-[#62617A] hover:bg-[#F3F2FA] hover:text-[#333273]'}`} data-id="element-2560" data-mp="9hjaiq1wg0qr">
                      Arabic
                    </button>
                  </div>
                </div>
              </div>

              <div className={`relative group cursor-pointer flex items-center`} onMouseEnter={() => handleMouseEnter('account')} onMouseLeave={() => handleMouseLeave('account')} onClick={() => navigate('/account')} data-id="element-2561" data-mp="1tx7ogpnz7nde">
                <UserIcon className="w-6 h-6 text-[#62617A] group-hover:text-[#333273] transition-colors" strokeWidth={1.5} data-id="element-2562" data-mp="2hms8lbvv8qo9" />
                {showAccountMenu && <div className="absolute top-full right-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-[#DEDCF0] z-50 py-2" data-id="element-2563" data-mp="2cu8u8cpctrie">
                    <div className="px-4 py-3 border-b border-[#DEDCF0]" data-id="element-2564" data-mp="35decqs2r1qsz">
                      <p className="text-sm font-normal text-[#333273]" data-id="element-2565" data-mp="30m67m88gcrlm">Hello, Guest</p>
                      <button onClick={() => setAuthModalOpen(true)} className="text-xs text-[#333273] font-normal mt-1 hover:underline" data-id="element-2566" data-mp="30kcxuk2jlr9e">
                        Sign In / Register
                      </button>
                    </div>
                    <div className="py-1" data-id="element-2567" data-mp="2g520ji4dxxtd">
                      <button onClick={() => navigate('/account')} className="flex items-center px-4 py-2 text-sm text-[#62617A] hover:bg-[#F3F2FA] hover:text-[#333273] w-full text-left transition-colors" data-id="element-2568" data-mp="2g64obh6evk52">
                        <UserIcon className="w-4 h-4 mr-3" strokeWidth={1.5} data-id="element-2569" data-mp="2u5cskohflig4" />
                        My Account
                      </button>
                      <button onClick={() => navigate('/account/orders')} className="flex items-center px-4 py-2 text-sm text-[#62617A] hover:bg-[#F3F2FA] hover:text-[#333273] w-full text-left transition-colors" data-id="element-2570" data-mp="2z23737dl85zf">
                        <PackageIcon className="w-4 h-4 mr-3" strokeWidth={1.5} data-id="element-2571" data-mp="1g3yi9pym21sr" />
                        My Orders
                      </button>
                    </div>
                  </div>}
              </div>

              <div className={`relative group cursor-pointer flex items-center`} onClick={() => navigate('/account/wishlist')} data-id="element-2572" data-mp="1ozo39xw5cu5m">
                <div className="relative" data-id="element-2573" data-mp="x6xaxyhl3pln">
                  <HeartIcon className="w-6 h-6 text-[#62617A] group-hover:text-[#333273] transition-colors" strokeWidth={1.5} data-id="element-2574" data-mp="s7keqn4tp66e" />
                  <span className="absolute -top-1.5 -right-1.5 bg-[#FFC400] text-[#333273] text-[9px] font-normal rounded-full w-4 h-4 flex items-center justify-center border border-white" data-id="element-2575" data-mp="1684yblikb60y">
                    {wishlistCount}
                  </span>
                </div>
              </div>

              <div className={`relative group cursor-pointer flex items-center`} onMouseEnter={() => handleMouseEnter('cart')} onMouseLeave={() => handleMouseLeave('cart')} onClick={() => navigate('/cart')} data-id="element-2576" data-mp="2cnxfc83k63jk">
                <div className="relative" data-id="element-2577" data-mp="1j9whmt2i3dz8">
                  <ShoppingCartIcon className="w-6 h-6 text-[#62617A] group-hover:text-[#333273] transition-colors" strokeWidth={1.5} data-id="element-2578" data-mp="1jaxvf5awiekn" />
                  <span className="absolute -top-1.5 -right-1.5 bg-[#FFC400] text-[#333273] text-[9px] font-normal rounded-full w-4 h-4 flex items-center justify-center border border-white" data-id="element-2579" data-mp="rj8itnoaukoq">
                    {cartCount()}
                  </span>
                </div>
                {showCartMenu && cartItems.length > 0 && <div className="absolute top-full right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-100 z-50 p-4" data-id="element-2580" data-mp="15awuxqyjt95p">
                    <div className="max-h-60 overflow-y-auto mb-4" data-id="element-2581" data-mp="7uze6e5kkm8c">
                      {cartItems.map(item => <div key={item.id} className="flex gap-3 mb-3" data-id="element-2582" data-mp="crzk8p2f5ozg">
                          <img src={item.image} className="w-12 h-12 rounded object-cover" data-id="element-2583" data-mp="5a1pjqowciga" />
                          <div className="flex-1 min-w-0" data-id="element-2584" data-mp="3rh00mt7qnw0t">
                            <p className="text-xs font-normal truncate" data-id="element-2585" data-mp="3ngg1jk3luic1">
                              {item.name}
                            </p>
                            <p className="text-xs text-gray-500" data-id="element-2586" data-mp="3ii4qx75hk3sr">
                              {item.quantity} x {item.price} SAR
                            </p>
                          </div>
                        </div>)}
                    </div>
                    <div className="border-t pt-3" data-id="element-2587" data-mp="33sys3kak8fle">
                      <div className="flex justify-between font-bold mb-3" data-id="element-2588" data-mp="33qdqmwe6c1o6">
                        <span data-id="element-2589" data-mp="2zqbypey8qmm3">Total:</span>
                        <span data-id="element-2590" data-mp="2urs9n5ocoupi">{cartTotalValue.toFixed(2)} SAR</span>
                      </div>
                      <button onClick={() => navigate('/checkout')} className="w-full bg-[#1E2B55] text-white py-2 rounded-lg font-bold text-sm" data-id="element-2591" data-mp="mj2mynea8vui">
                        Checkout
                      </button>
                    </div>
                  </div>}
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Navigation Bar */}
      <motion.div initial={false} animate={{
      height: scrolled ? 0 : 52,
      opacity: scrolled ? 0 : 1
    }} transition={{
      duration: 0.3
    }} className="bg-white border-b border-[#E3E1EF] overflow-hidden" data-id="element-2592" data-mp="3rt2xk6vyooo">
        <div className="container mx-auto px-4" data-id="element-2593" data-mp="3uzxe55lpwrsr">
          <div className="flex h-[52px] items-center gap-5 overflow-x-auto no-scrollbar" data-id="element-2594" data-mp="3uwjazv58cek9">
            <button onClick={() => navigate('/categories')} className="flex flex-shrink-0 items-center gap-2 rounded-md bg-[#1E2B55] px-3 py-2 text-[13px] font-semibold text-white transition-colors duration-150 hover:bg-[#162041]" data-id="element-2595" data-mp="3h17gnouzkq3v">
              <MenuIcon className="h-4 w-4" strokeWidth={1.8} data-id="element-2596" data-mp="2xevukmz0nkc0" /> All Categories
            </button>
            <nav className="flex flex-shrink-0 items-center gap-6 whitespace-nowrap" data-id="element-2597" data-mp="3benhjd7igb25">
              {['Proteins', 'Vitamins', 'Pre-Workout', 'Weight Management'].map(item => <button key={item} onClick={() => navigate(`/category/${item.toLowerCase().replace(/ /g, '-')}`)} className="text-[13px] font-medium text-[#62617A] transition-colors duration-150 hover:text-[#333273]" data-id="element-2598" data-mp="3l9p4c2ebd11k">
                    {item}
                  </button>)}
              <button onClick={() => navigate('/category/best-sellers')} className="text-[13px] font-medium text-[#62617A] transition-colors duration-150 hover:text-[#333273]" data-id="element-2599" data-mp="2ybv1a0pt6zz">
                Best Sellers
              </button>
              <button onClick={() => navigate('/rx')} className="text-[13px] font-medium text-[#62617A] transition-colors duration-150 hover:text-[#333273]" data-id="element-2600" data-mp="pdyaq4pshzw4">
                RX Health
              </button>
              <button onClick={() => navigate('/offers')} className="rounded-md bg-[#fff8e6] px-2.5 py-1.5 text-[13px] font-bold text-[#8a5a00] transition-colors duration-150 hover:bg-[#ffedbf]" data-id="element-2601" data-mp="3sv6oacdhidl1">
                Deals &amp; Offers
              </button>
            </nav>
          </div>
        </div>
      </motion.div>

      <AuthModal isOpen={authModalOpen} onClose={() => setAuthModalOpen(false)} data-id="element-2602" data-mp="3a3nosfio5hnr" />
      <LocationModal isOpen={locationModalOpen} onClose={() => setLocationModalOpen(false)} onSelectLocation={setSelectedLocation} data-id="element-2603" data-mp="354b15sjz46k4" />
      <OrderTrackingModal isOpen={orderTrackingModalOpen} onClose={() => setOrderTrackingModalOpen(false)} data-id="element-2604" data-mp="1pe0efifcara9" />
    </div>;
}